import { Channel } from 'spyne';
import { AcmeInvoicesChannelTraits } from 'traits/db/db-invoices-channel-traits.js';

/**
 * Invoice matching and the message boundary for pagination requests/results.
 *
 * Separate from CHANNEL_ACME_DATA because the two answer different questions.
 * That channel holds what the server said; this one resolves what the current
 * URL asks for out of it. Neither needs the other's job.
 *
 * ── window.location is search state ─────────────────────────────────────────
 *
 * This channel holds no `query` and no pagination state. It emits a search
 * instruction that InvoicesTableView applies to the URL, then learns the result through
 * CHANNEL_WINDOW like any other observer. A bookmark, a back button and a
 * keystroke therefore arrive by one path and are indistinguishable — which is
 * the reason the write lives in the view rather than here.
 *
 * ── replay ─────────────────────────────────────────────────────────────────
 *
 * True: this is a current-value channel. A view mounting later needs the latest
 * domain or routing result, not merely the next change.
 * [choose-replay-semantics]
 *
 * All behaviour lives in AcmeInvoicesChannelTraits — this class is structure and
 * event flow only.
 */
export class ChannelAcmeInvoices extends Channel {
  constructor(name, props = {}) {
    name = 'CHANNEL_ACME_INVOICES';
    props.replay = true;
    props.traits = [AcmeInvoicesChannelTraits];
    super(name, props);
  }

  onRegistered() {
    this.acmeInvoices$OnRegistered();
  }

  addRegisteredActions() {
    return [
      // An instruction to change the URL, consumed only by InvoicesTableView.
      // Nothing else should act on it — the resulting list arrives on the action
      // below, after the URL has actually moved.
      'CHANNEL_ACME_INVOICES_UPDATE_PARAMS_EVENT',

      // Every invoice id matching the current query, plus the authoritative set.
      'CHANNEL_ACME_INVOICES_LIST_EVENT',

      // One invoice's status changed in the held data — optimistic apply,
      // rollback and authoritative refresh all arrive as this same action, so
      // a mounted row repaints its pill by one path. Emitted synchronously
      // BEFORE the LIST emission of the same data change, so the channel's
      // replay cache never ends a turn holding this partial payload.
      'CHANNEL_ACME_INVOICES_STATUS_EVENT',

      // One invoice's DISPLAYED cell values changed because its unsaved-edit
      // overlay did — a commit, an undo/redo, a discard, or a save settling.
      // Same contract as STATUS_EVENT: per-invoice, consumed by the mounted
      // row through its own-id filter, and emitted synchronously BEFORE the
      // LIST emission of the same change so the replay cache never ends a
      // turn holding this partial payload.
      'CHANNEL_ACME_INVOICES_CELLS_EVENT',
      'CHANNEL_ACME_INVOICES_CREATE_EVENT',
      'CHANNEL_ACME_INVOICES_EDIT_EVENT',
      // Transmitted by a view that needs this channel's form navigation
      // performed without a click — the quick-search overlay's Enter
      // activation. Relayed in onViewStreamInfo to the same handler the
      // CHANNEL_UI clicks reach, so both paths stay one code path.
      'CHANNEL_ACME_INVOICES_NAVIGATE_EVENT',
      // A validated response to a pagination control's CHANNEL_UI request.
      'CHANNEL_ACME_INVOICES_PAGINATION_EVENT',
      // A pagination ViewStream result relayed for the isolated table view.
      'CHANNEL_ACME_INVOICES_VISIBLE_IDS_EVENT',

      // The row/card breakpoint, conformed from the WINDOW mediaQuery. The
      // flag also rides every LIST and VISIBLE_IDS payload; this action exists
      // for LIVE crossings while a table is already mounted.
      'CHANNEL_ACME_INVOICES_DISPLAY_EVENT',
    ];
  }

  onViewStreamInfo(e) {
    this.acmeInvoices$OnViewStreamInfo(e);
  }
}
